/**
 * Keyboard shortcuts hook for gallery review workflow.
 *
 * Provides keyboard-first review experience:
 * - A: Approve current image
 * - R: Reject current image
 * - F: Flag for review
 * - I: Toggle include/exclude
 * - C: Set as cover
 * - Arrow keys: Navigate images
 * - Escape: Close lightbox / exit mode
 *
 * Shortcuts are disabled when:
 * - Focus is in an input/textarea/select
 * - enabled prop is false
 * - Component is unmounted
 *
 * @see /src/features/gallery/pages/GalleryReviewPage.tsx - Main consumer
 */

import { useEffect, useCallback, useRef } from 'react'

/**
 * Configuration for keyboard shortcuts.
 * All callbacks are optional - only bind what you need.
 */
export interface KeyboardShortcutConfig {
  /** Approve current image (A key) */
  onApprove?: () => void
  /** Reject current image (R key) */
  onReject?: () => void
  /** Flag for review (F key) */
  onFlag?: () => void
  /** Toggle include/exclude (I key) */
  onToggleInclude?: () => void
  /** Set as cover image (C key) */
  onSetCover?: () => void
  /** Navigate to next image (Right arrow) */
  onNext?: () => void
  /** Navigate to previous image (Left arrow) */
  onPrev?: () => void
  /** Close/exit action (Escape) */
  onEscape?: () => void
  /** Enable/disable shortcuts */
  enabled?: boolean
}

/**
 * Check if the current focus is in an input element.
 * We don't want shortcuts firing when user is typing.
 */
function isInputFocused(): boolean {
  const activeElement = document.activeElement
  if (!activeElement) return false

  const tagName = activeElement.tagName.toLowerCase()
  const isEditable = activeElement.getAttribute('contenteditable') === 'true'

  return (
    tagName === 'input' ||
    tagName === 'textarea' ||
    tagName === 'select' ||
    isEditable
  )
}

/**
 * Hook for binding keyboard shortcuts in gallery review.
 *
 * @example
 * ```tsx
 * useKeyboardShortcuts({
 *   onApprove: () => approveImage(currentId),
 *   onReject: () => rejectImage(currentId),
 *   onNext: () => setCurrentIndex(i => i + 1),
 *   onPrev: () => setCurrentIndex(i => i - 1),
 *   onEscape: () => closeLightbox(),
 *   enabled: !isLoading,
 * })
 * ```
 */
export function useKeyboardShortcuts(config: KeyboardShortcutConfig): void {
  // Use ref to always have latest callbacks without re-registering listener
  const configRef = useRef(config)
  configRef.current = config

  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    const {
      onApprove,
      onReject,
      onFlag,
      onToggleInclude,
      onSetCover,
      onNext,
      onPrev,
      onEscape,
      enabled = true,
    } = configRef.current

    // Skip if disabled or typing in input
    if (!enabled || isInputFocused()) return

    // Normalize key to lowercase for consistent matching
    const key = event.key.toLowerCase()

    switch (key) {
      case 'a':
        if (onApprove) {
          event.preventDefault()
          onApprove()
        }
        break

      case 'r':
        if (onReject) {
          event.preventDefault()
          onReject()
        }
        break

      case 'f':
        if (onFlag) {
          event.preventDefault()
          onFlag()
        }
        break

      case 'i':
        if (onToggleInclude) {
          event.preventDefault()
          onToggleInclude()
        }
        break

      case 'c':
        if (onSetCover) {
          event.preventDefault()
          onSetCover()
        }
        break

      case 'arrowright':
        if (onNext) {
          event.preventDefault()
          onNext()
        }
        break

      case 'arrowleft':
        if (onPrev) {
          event.preventDefault()
          onPrev()
        }
        break

      case 'escape':
        if (onEscape) {
          event.preventDefault()
          onEscape()
        }
        break
    }
  }, [])

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])
}

/**
 * Keyboard shortcut hints for display in UI.
 * Use with ShortcutHint component.
 */
export const SHORTCUT_HINTS = [
  { key: 'A', label: 'Approve', action: 'approve' },
  { key: 'R', label: 'Reject', action: 'reject' },
  { key: 'F', label: 'Flag', action: 'flag' },
  { key: 'I', label: 'Include', action: 'include' },
  { key: 'C', label: 'Cover', action: 'cover' },
  { key: '←', label: 'Previous', action: 'prev' },
  { key: '→', label: 'Next', action: 'next' },
  { key: 'Esc', label: 'Close', action: 'escape' },
] as const

export type ShortcutAction = typeof SHORTCUT_HINTS[number]['action']
